import styled from '@emotion/styled'
import { LogoMenu } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { HeaderContext } from 'app/containers/Header'
import { styles } from 'app/theme'
import useMediaQuery from 'app/utils/useMediaQuery'
import { useVocabularyData } from 'app/utils/vocabulary'
import { circOut, motion } from 'framer-motion'
import React, { memo, useContext } from 'react'

import { Contacts } from './Contacts'
import { MenuItem, Props as MenuItemProps } from './MenuItem'

export interface Props {
  languageCode: string
  menuItems: MenuItemProps[]
  modalStatus?: boolean
  startAnimation?: boolean
}

export const Navigation = memo(function Navigation({
  menuItems,
  modalStatus,
  startAnimation,
}: Props) {
  const { languageCode, languagePrefix } = useContext(HeaderContext) as any

  if (!menuItems) {
    return null
  }

  const isMobile = useMediaQuery('(max-width: 1199px)')

  const colMenuItems = Math.ceil(menuItems.length / 3)

  const variants = {
    visible: (i: any) => ({
      opacity: 1,
      y: 0,
      transition: {
        bounce: 0,
        delay: isMobile ? 0 : i * 0.1,
        duration: 1,
        ease: circOut,
        type: 'spring',
      },
    }),
    hidden: {
      opacity: 0,
      y: 60,
      transition: {
        delay: 0,
        duration: 0,
      },
    },
  }

  return (
    <Container
      className={modalStatus ? 'open' : undefined}
      onClick={(e) => e.stopPropagation()}
      row
      stretch
    >
      <LogoMenu />
      <LeftSide space="between">
        <Nav>
          <NavList>
            {menuItems.slice(0, colMenuItems + 1).map((item, index) => {
              return (
                <NavLi
                  key={index}
                  custom={index}
                  animate={modalStatus && startAnimation ? 'visible' : 'hidden'}
                  variants={variants}
                >
                  <MenuItem {...item} />
                </NavLi>
              )
            })}
          </NavList>

          <NavList>
            {menuItems
              .slice(colMenuItems + 1, menuItems.length - 1)
              .map((item, index) => {
                return (
                  <NavLi
                    key={index}
                    custom={index}
                    animate={
                      modalStatus && startAnimation ? 'visible' : 'hidden'
                    }
                    variants={variants}
                  >
                    <MenuItem {...item} />
                  </NavLi>
                )
              })}
          </NavList>

          <NavList>
            {menuItems.slice(menuItems.length - 1).map((item, index) => {
              return (
                <NavLi
                  key={index}
                  custom={index}
                  animate={modalStatus && startAnimation ? 'visible' : 'hidden'}
                  variants={variants}
                >
                  <MenuItem {...item} />
                  <BookingCTA
                    className="item-book-cta"
                    href={`https://www.simplebooking.it/ibe2/hotel/9844?lang=${languagePrefix ? languagePrefix : 'it'}`}
                    rel="noopener"
                    target="_blank"
                  >
                    {useVocabularyData('book', languageCode)}
                  </BookingCTA>
                </NavLi>
              )
            })}
          </NavList>
        </Nav>

        <Contacts
          modalStatus={modalStatus}
          startAnimation={startAnimation}
          languageCode={languageCode}
        />
      </LeftSide>

      <RightSide className={modalStatus ? 'visible' : undefined} />
    </Container>
  )
})

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralDark3};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;

  > svg {
    position: absolute;
    top: 1.875rem;
    left: 50%;
    transform: translateX(-50%);
    width: 7.125rem;
    height: 3.25rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
  }

  opacity: 0;
  transform: translateY(-100%);

  > div:last-of-type {
    opacity: 0;
    transition: 0.6s ease-in-out;
  }

  &.open {
    transition: 0.6s ease-in-out;
    opacity: 1;
    transform: none;

    > div:last-of-type {
      opacity: 1;
      transition: 0.6s 0.6s ease-in-out;
    }
  }

  @media (max-width: 1199px) {
    > svg {
      top: 1.125rem;
      width: 4.875rem;
      height: 2.25rem;
    }
  }
`

const LeftSide = styled(FlexBox)`
  width: 60.069vw;
  position: relative;

  @media (max-width: 1199px) {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    transform: none;
  }

  .item-contacts {
    position: absolute;
    bottom: 3.75rem;
    right: 0;
    left: 4.167vw;
  }
`

const Nav = styled.nav`
  display: flex;
  position: absolute;
  top: calc(15.556vh - 3.75rem);
  right: 0;
  left: 4.167vw;

  @media (max-width: 1199px) {
    flex-direction: column;
    top: 4.5rem;
    left: 0;
    padding-left: 2.25rem;
    border-top: 0.0625rem solid
      ${({ theme }) => theme.colors.variants.primaryLight};
    padding-top: 1.375rem;
  }
  @media (max-width: 767px) {
    padding-bottom: 1.875rem;
    top: 4.375rem;
  }
`

const NavList = styled.ul`
  margin-right: 9.5vw;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const NavLi = styled(motion.li)`
  margin-top: 3.75rem;

  .item-book-cta {
    margin-top: 0.625rem;
    display: block;
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

    &:hover {
      transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
      opacity: 0.6;
    }
  }

  @media (max-width: 1199px) {
    margin-top: 1.5rem;

    .item-book-cta {
      margin-top: 1.5rem;
    }
  }
`

const BookingCTA = styled.a`
  ${styles.description.medium}

  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  padding-bottom: 0.125rem;
  position: relative;
  font-size: 1rem;
  font-weight: 300;
  padding: 0;
  line-height: 1.25rem;

  @media (max-width: 1199px) {
    ${styles.title.small}

    font-size: 2rem;
    line-height: 2rem;
    text-transform: uppercase;
    display: inline-block;
    color: ${({ theme }) => theme.colors.variants.neutralLight4};
    padding-bottom: 0.125rem;
    position: relative;
    transition: 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  }
`

const RightSide = styled.div`
  width: 35.764vw;
  background: url('/navigation.webp') no-repeat center;
  background-size: cover;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  bottom: 3.75rem;
  top: 15.556vh;
  right: 4.167vw;
  transition: 0.6s ease-in-out;
  z-index: 2;
  &.visible {
    opacity: 1;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`
